import { FormikContext } from "formik";
import { isEqual } from "lodash";
import queryString from "query-string";
import React, {
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from "react";
import PillarTableFilters, {
  type PillarTableFiltersProps,
  type PillarTableFilterComponentType,
} from "shared/components/pillar-table/filters/PillarTableFilters";
import { useCustomRouter } from "shared/context/CustomRouterContext";
import { type FiltersRequest } from "shared/filter-where-clause";
import { flattenChildComponents } from "shared/helpers/react/flatten-child-components";

const usePillarTableFiltersValues = ({
  children,
  urlParams,
}: {
  children: React.ReactNode;
  urlParams?: boolean;
}) => {
  const router = useCustomRouter();

  const formik = useContext(FormikContext);
  const filterComponent = useMemo(
    () =>
      React.Children.toArray(children).find(
        (child) =>
          React.isValidElement<PillarTableFiltersProps>(child) &&
          child.type === PillarTableFilters,
      ) as React.ReactElement<PillarTableFiltersProps> | undefined,
    [children],
  );

  const filterComponents = flattenChildComponents(filterComponent)
    .filter(
      (
        child,
      ): child is React.ReactElement<{
        filterKey: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        defaultValue?: any;
      }> => React.isValidElement(child) && isFilterComponentType(child.type),
    )
    .reduce((acc: FiltersRequest, curr) => {
      const filterKey = curr.props.filterKey;
      // Pull defaultValue from the rendered element's props first (e.g.
      // `<PillarTable.FilterSelectMenu defaultValue="false" />`) and fall back
      // to a static `defaultValue` declared on the component type, which keeps
      // backwards compatibility with filter component classes that expose one.
      const propDefaultValue = curr.props?.defaultValue;
      const { defaultValue: typeDefaultValue } =
        curr.type as PillarTableFilterComponentType;
      const defaultValue =
        propDefaultValue !== undefined ? propDefaultValue : typeDefaultValue;

      if (defaultValue !== undefined) {
        return { ...acc, [filterKey]: defaultValue };
      }
      return { ...acc, [filterKey]: "" };
    }, {});

  const filtersFromUrl = (
    router: ReturnType<typeof useCustomRouter>,
    filterComponents: FiltersRequest,
    urlParams?: boolean,
  ): FiltersRequest => {
    if (!urlParams) {
      return filterComponents;
    }
    const query = queryString.parseUrl(router.asPath, {
      arrayFormat: "bracket",
    });
    // Update filter components with values from URL. Only overwrite a filter's
    // default when the URL actually contains that key, otherwise a missing key
    // would clobber a prop-based default (e.g. `defaultValue="false"`) with
    // `undefined`, causing an extra unfiltered fetch when the filter hook's
    // useEffect later seeds the default value.
    for (const key in filterComponents) {
      if (!(key in query.query)) {
        continue;
      }
      if (query.query[key] === null) {
        // handle null value
      } else if (Array.isArray(query.query[key])) {
        filterComponents[key] = (query.query[key] as []).filter(
          (item) => item !== null,
        );
      } else {
        filterComponents[key] = query.query[key] as string;
      }
    }
    return filterComponents;
  };

  const [filtersRequest, setFilters] = useState<FiltersRequest>(
    filtersFromUrl(router, filterComponents, urlParams),
  );

  const [filtersRendered, setFiltersRendered] = useState(
    Object.keys(filterComponents).length > 0 ? false : true,
  );

  useEffect(() => {
    if (!filtersRendered) {
      setFiltersRendered(true);
    }
    if (formik) {
      formik.setFieldValue("filtersRequest", filtersRequest);
    }
  }, [filtersRequest]);

  const setFiltersFun = (incomingFilters: FiltersRequest) => {
    // Sanitize filters
    const sanitizedFilters = Object.keys(incomingFilters).reduce(
      (acc: FiltersRequest, curr: string) => {
        const value = incomingFilters[curr];
        if (
          (Array.isArray(value) && value.length === 0) ||
          value === "" ||
          value === undefined
        ) {
          return acc; // Skip empty values
        }
        return { ...acc, [curr]: value };
      },
      {},
    );
    if (!isEqual(sanitizedFilters, filtersRequest)) {
      setFilters(sanitizedFilters);
    }
  };

  const FilterComponentWrapper = useCallback(
    () => (filterComponent ? filterComponent : <PillarTableFilters />),
    [filterComponent],
  );
  return {
    FilterComponentWrapper,
    filtersRequest,
    setFilters: setFiltersFun,
    filtersRendered,
  };
};

export default usePillarTableFiltersValues;

const isFilterComponentType = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  componentType: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchObject: any = PillarTableFilters,
): boolean => {
  for (const key in searchObject) {
    if (
      Object.prototype.hasOwnProperty.call(searchObject, key) &&
      typeof searchObject[key] === "function"
    ) {
      if (componentType === searchObject[key]) {
        return true;
      }
      if (
        typeof searchObject[key] === "function" &&
        searchObject[key] !== null
      ) {
        if (isFilterComponentType(componentType, searchObject[key])) {
          return true;
        }
      }
    }
  }

  return false;
};
